import React, { useEffect } from 'react';
import InputBox from './InputBox';
import VirtualKeyboard from './VirtualKeyboard';
import usePracticeSession from '../hooks/usePracticeSession';
import { usePractice } from '../contexts/PracticeContext';
import confetti from 'canvas-confetti';

interface PracticeViewProps {
  practice: any;
  onBack: () => void;
  onShowResults: (results: { correct: number; incorrect: number }) => void;
  onBackToResults: () => void;
}

interface PracticeSession {
  currentWord: string;
  userInput: string[];
  activeInputIndex: number;
  feedback: {
    visible: boolean;
    correct: boolean;
    correctWord: string;
    attemptCount: number;
  };
  inputRefs: React.RefObject<Array<HTMLInputElement | null>>;
  isPronouncing: boolean;
  pronunciationStatus: string;
  correctCount: number;
  incorrectCount: number;
  showKeyboard: boolean;
  pronounceWord: () => void;
  checkAnswer: () => void;
  skipWord: () => void;
  handleInputChange: (index: number, value: string) => void;
  handleKeyDown: (event: React.KeyboardEvent, index: number) => void;
  handleInputClick: (index: number) => void;
  handleKeyPress: (key: string) => void;
  setShowKeyboard: (show: boolean) => void;
}

const PracticeView: React.FC<PracticeViewProps> = ({
  practice,
  onBack,
  onShowResults,
  onBackToResults
}) => {
  const { updatePracticeResults } = usePractice();
  const practiceSession: PracticeSession = usePracticeSession({
    initialPractice: practice,
    onComplete: (correctCount, incorrectCount) => {
      onShowResults({ correct: correctCount, incorrect: incorrectCount });
    },
    onUpdateResults: (practiceId, isCorrect) => {
      // Trigger confetti for correct answers
      if (isCorrect) {
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.6 }
        });
      }
      updatePracticeResults(practiceId, isCorrect);
    }
  });

  // Trigger welcome confetti when the component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.3 }
      });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      {/* Header is now rendered in App.tsx to avoid duplication */}
      
      {/* Progress indicator */}
      <div className="bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 text-white p-4 rounded-xl mb-6 shadow-lg">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-2xl mr-2">🎯</span>
            <span className="font-bold">Words: {practiceSession.correctCount + practiceSession.incorrectCount} / {practice?.words?.length || 0}</span>
          </div>
          <div>
            <span className="text-2xl mr-2">✅</span>
            <span className="font-bold">Correct: {practiceSession.correctCount}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border-b-4 border-indigo-500">
        <div className="flex justify-center mb-8">
          <button
            onClick={practiceSession.pronounceWord}
            className={`
              px-8 py-4 rounded-full flex items-center justify-center text-lg font-bold shadow-md
              ${practiceSession.isPronouncing ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white transform hover:scale-105'}
              transition-all
            `}
            disabled={practiceSession.isPronouncing}
          >
            {practiceSession.isPronouncing ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading sound...
              </span>
            ) : (
              <span className="flex items-center">
                <span className="text-2xl mr-3">🔊</span>
                Hear the Word
              </span>
            )}
          </button>
        </div>

        {practiceSession.pronunciationStatus === 'error' && (
          <div className="text-center p-3 bg-red-100 rounded-lg text-red-600 mb-4 flex items-center justify-center">
            <span className="text-xl mr-2">😕</span>
            <span>Oops! Couldn't play the sound. Try again!</span>
          </div>
        )}

        {practiceSession.feedback.visible && (
          <div className={`mb-6 p-6 rounded-xl shadow-md ${practiceSession.feedback.correct ? 'bg-green-100 border-green-300 border-2' : 'bg-red-100 border-red-300 border-2'}`}>
            <div className="flex items-center justify-center">
              <span className="text-4xl mr-3">
                {practiceSession.feedback.correct ? '🎉' : (practiceSession.feedback.attemptCount > 2 ? '🤔' : '😮')}
              </span>
              <span className="font-bold text-xl">
                {practiceSession.feedback.correct ? 
                  (practiceSession.feedback.attemptCount === 0 ? 
                    'Awesome job! You got it on your first try!' : 
                    `Great job! You got it after ${practiceSession.feedback.attemptCount} attempts!`) : 
                  `That's not quite right. Try spelling "${practiceSession.feedback.correctWord}" again! (Attempt ${practiceSession.feedback.attemptCount})`
                }
              </span>
            </div>
          </div>
        )}

        <InputBox
          word={practiceSession.currentWord}
          userInput={practiceSession.userInput}
          activeInputIndex={practiceSession.activeInputIndex}
          feedback={practiceSession.feedback}
          handleInputChange={practiceSession.handleInputChange}
          handleKeyDown={(e) => practiceSession.handleKeyDown(e, practiceSession.activeInputIndex)}
          handleInputClick={practiceSession.handleInputClick}
        />

        <div className="flex flex-wrap gap-3 justify-center mt-8">
          <button
            id="back-button"
            onClick={onBack}
            className="px-6 py-3 bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-lg font-bold rounded-full hover:from-blue-500 hover:to-indigo-600 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 transition-transform flex items-center"
          >
            <span className="text-xl mr-2">⬅️</span>
            Back to List
          </button>
          <button
            id="skip-button"
            onClick={() => practiceSession.skipWord()}
            className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-lg font-bold rounded-full hover:from-yellow-500 hover:to-orange-600 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 transition-transform flex items-center"
            disabled={practiceSession.feedback.visible}
          >
            <span className="text-xl mr-2">⏭️</span>
            Skip this Word
          </button>
        </div>

        {/* Integrated keyboard */}
        <div className="mt-6 border-t-2 border-indigo-100 pt-4">
          <VirtualKeyboard
            onKeyPress={practiceSession.handleKeyPress}
            disabled={practiceSession.feedback.visible}
            currentWord={practiceSession.currentWord}
          />
        </div>
      </div>
    </div>
  );
};

export default PracticeView;
